import React, { useEffect, useRef, createElement } from 'react';
import { Theme } from '../hooks/useTheme';
interface ParticleHeroProps {
  theme: Theme;
}
export function ParticleHero({
  theme
}: ParticleHeroProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!canvas || !overlay) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let width = window.innerWidth;
    let height = window.innerHeight;
    // Guard against invalid dimensions
    if (width === 0 || height === 0) {
      return;
    }
    let particles: Particle[] = [];
    let targetPoints: {
      x: number;
      y: number;
    }[] = [];
    let scrollProgress = 0;
    let mouse = {
      x: -1000,
      y: -1000
    };
    // Configuration
    const particleCount = width < 768 ? 400 : 800;
    const connectionDistance = 100;
    const mouseDistance = 60; // Reduced from 150 to 60 for more subtle mouse interaction
    canvas.width = width;
    canvas.height = height;
    // Load image based on theme
    const imageUrl = theme === 'dark' ? 'https://cdn.magicpatterns.com/uploads/sGpsJmUoty42325s8SvDQ6/avatar-outline-dark.png' : 'https://cdn.magicpatterns.com/uploads/uxpJytHA5HntAkw1QWBmpR/avater-outline-light.png';
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.src = imageUrl;
    image.onload = () => {
      extractPointsFromImage();
      init();
      animate();
    };
    function extractPointsFromImage() {
      // Additional safety check before getImageData
      if (width === 0 || height === 0) {
        return;
      }
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;
      // Increased scale to make avatar larger (was 0.6, now 0.75)
      const scale = Math.min(width, height) * 0.75;
      const imgWidth = scale;
      const imgHeight = scale * (image.height / image.width);
      tempCanvas.width = width;
      tempCanvas.height = height;
      const offsetX = (width - imgWidth) / 2;
      const offsetY = (height - imgHeight) / 2;
      tempCtx.drawImage(image, offsetX, offsetY, imgWidth, imgHeight);
      const imageData = tempCtx.getImageData(0, 0, width, height);
      const data = imageData.data;
      const potentialPoints: {
        x: number;
        y: number;
      }[] = [];
      for (let y = 0; y < height; y += 4) {
        for (let x = 0; x < width; x += 4) {
          const index = (y * width + x) * 4;
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const a = data[index + 3];
          if (a > 128 && (r < 200 || g < 200 || b < 200)) {
            potentialPoints.push({
              x,
              y
            });
          }
        }
      }
      for (let i = potentialPoints.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [potentialPoints[i], potentialPoints[j]] = [potentialPoints[j], potentialPoints[i]];
      }
      targetPoints = potentialPoints.slice(0, particleCount);
    }
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      baseX: number;
      baseY: number;
      targetX: number;
      targetY: number;
      density: number;
      constructor(target?: {
        x: number;
        y: number;
      }) {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 1.5 + 0.5;
        this.baseX = this.x;
        this.baseY = this.y;
        this.targetX = target ? target.x : this.x;
        this.targetY = target ? target.y : this.y;
        this.density = Math.random() * 30 + 1;
      }
      update() {
        const formationThreshold = height * 0.4;
        const formationProgress = Math.min(Math.max(scrollProgress / formationThreshold, 0), 1);
        const ease = formationProgress < 0.5 ? 2 * formationProgress * formationProgress : 1 - Math.pow(-2 * formationProgress + 2, 2) / 2;
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const forceDirectionX = dx / distance;
        const forceDirectionY = dy / distance;
        const maxDistance = mouseDistance;
        const force = (maxDistance - distance) / maxDistance;
        const directionX = forceDirectionX * force * this.density;
        const directionY = forceDirectionY * force * this.density;
        if (distance < mouseDistance) {
          this.x -= directionX;
          this.y -= directionY;
        } else {
          if (formationProgress > 0.01 && this.targetX !== undefined) {
            const currentTargetX = this.baseX + (this.targetX - this.baseX) * ease;
            const currentTargetY = this.baseY + (this.targetY - this.baseY) * ease;
            const dx = currentTargetX - this.x;
            const dy = currentTargetY - this.y;
            this.x += dx * 0.1;
            this.y += dy * 0.1;
          } else {
            if (this.x !== this.baseX) {
              const dx = this.x - this.baseX;
              this.x -= dx / 10;
            }
            if (this.y !== this.baseY) {
              const dy = this.y - this.baseY;
              this.y -= dy / 10;
            }
            this.baseX += this.vx;
            this.baseY += this.vy;
            if (this.baseX < 0 || this.baseX > width) this.vx = -this.vx;
            if (this.baseY < 0 || this.baseY > height) this.vy = -this.vy;
          }
        }
      }
      draw() {
        if (!ctx) return;
        const formationThreshold = height * 0.4;
        const formationProgress = Math.min(Math.max(scrollProgress / formationThreshold, 0), 1);
        const alpha = 0.3 + formationProgress * 0.5;
        const particleColor = theme === 'dark' ? '255, 255, 255' : '0, 0, 0';
        ctx.fillStyle = `rgba(${particleColor}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
      }
    }
    function init() {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        const target = i < targetPoints.length ? targetPoints[i] : undefined;
        particles.push(new Particle(target));
      }
    }
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        const formationThreshold = height * 0.4;
        const formationProgress = Math.min(Math.max(scrollProgress / formationThreshold, 0), 1);
        if (formationProgress < 0.8) {
          for (let j = i; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < connectionDistance) {
              ctx.beginPath();
              const opacity = (0.15 - distance / connectionDistance * 0.15) * (1 - formationProgress);
              const lineColor = theme === 'dark' ? '255, 255, 255' : '0, 0, 0';
              ctx.strokeStyle = `rgba(${lineColor}, ${opacity})`;
              ctx.lineWidth = 1;
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
              ctx.closePath();
            }
          }
        }
      }
      requestAnimationFrame(animate);
    }
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      extractPointsFromImage();
      init();
    };
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.x;
      mouse.y = e.y;
    };
    const handleScroll = () => {
      scrollProgress = window.scrollY;
      // Show overlay at start of Writing section (approximately 1.5 viewport heights)
      const overlayThreshold = height * 1.5;
      const overlayProgress = Math.min(Math.max((scrollProgress - overlayThreshold) / (height * 0.3), 0), 1);
      if (overlay) {
        overlay.style.opacity = overlayProgress.toString();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [theme]);
  const imageUrl = theme === 'dark' ? 'https://cdn.magicpatterns.com/uploads/sGpsJmUoty42325s8SvDQ6/avatar-outline-dark.png' : 'https://cdn.magicpatterns.com/uploads/uxpJytHA5HntAkw1QWBmpR/avater-outline-light.png';
  return <>
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" style={{
      opacity: 0.8
    }} data-id="element-79" />
      <div ref={overlayRef} className="fixed inset-0 w-full h-full pointer-events-none z-0 flex items-center justify-center" style={{
      opacity: 0
    }} data-id="element-80">
        <img src={imageUrl} alt="Portrait outline" className="max-w-[75vmin] max-h-[90vmin] object-contain opacity-40" data-id="element-81" />
      </div>
    </>;
}